import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './SearchResults.css';

function SearchResults() {
  const [searchParams] = useSearchParams();
  const [results, setResults] = useState([]);
  const [size, setSize] = useState(0);
  const [startindex, setStartindex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  const q = searchParams.get('q') || 'all';
  const source = searchParams.get('source') || 'arxiv';
  const sortby = searchParams.get('sortby') || 'submittedDate';
  const sortOrder = searchParams.get('sortOrder') || 'descending';

  const handleSortChange = (newSortby) => {
    navigate(`/works/search?q=${encodeURIComponent(q)}&source=${encodeURIComponent(source)}&sortby=${encodeURIComponent(newSortby)}&sortOrder=${encodeURIComponent(sortOrder)}`);
  };

  const handleOrderChange = (newOrder) => {
    navigate(`/works/search?q=${encodeURIComponent(q)}&source=${encodeURIComponent(source)}&sortby=${encodeURIComponent(sortby)}&sortOrder=${encodeURIComponent(newOrder)}`);
  };

  const handleSourceChange = (newSource) => {
    navigate(`/works/search?q=${encodeURIComponent(q)}&source=${encodeURIComponent(newSource)}&sortby=${encodeURIComponent(sortby)}&sortOrder=${encodeURIComponent(sortOrder)}`);
  };

  useEffect(() => {
    const fetchResults = async () => {
      setLoading(true);
      setError(null);

      try {
        let response, xmlText;

        if (source === 'arxiv') {
          // Call arXiv API directly from the client
          const arxivUrl = `https://export.arxiv.org/api/query?search_query=all:${encodeURIComponent(q)}&sortBy=${encodeURIComponent(sortby)}&sortOrder=${encodeURIComponent(sortOrder)}&start=${currentPage * 10}`;
          response = await fetch(arxivUrl);
          xmlText = await response.text();
        } else if (source === 'biorxiv' || source === 'medrxiv') {
          // Call backend for bioRxiv/medRxiv (D1 database)
          response = await fetch(`/api/search?q=${encodeURIComponent(q)}&source=${encodeURIComponent(source)}&sortby=${encodeURIComponent(sortby)}&sortOrder=${encodeURIComponent(sortOrder)}&start=${currentPage * 10}`);
          const jsonData = await response.json();
          
          if (!response.ok) {
            throw new Error(jsonData.error || 'Database query failed');
          }

          // For database results, format them to match arXiv XML structure for display
          setResults(jsonData.map((paper, idx) => ({
            title: paper.title,
            summary: paper.abstract || 'No abstract available',
            authors: paper.authors ? paper.authors.split(',').map(a => a.trim()) : [],
            categories: ['biology'],
            published: paper.date || new Date().toISOString(),
            arxivId: `${source}/${paper.doi}`,
            source: source
          })));
          
          setSize(jsonData.length || 0);
          setStartindex(currentPage * 10 + 1);
          setLoading(false);
          return;
        } else {
          throw new Error('Invalid source');
        }

        // Parse XML response from arXiv
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(xmlText, 'text/xml');

        // Check for XML parsing errors
        if (xmlDoc.documentElement.nodeName === 'parsererror') {
          throw new Error('Failed to parse arXiv response');
        }

        // Extract entries
        const entries = Array.from(xmlDoc.getElementsByTagName('entry')).map(entry => {
          const title = entry.getElementsByTagName('title')[0]?.textContent || 'No title';
          const summary = entry.getElementsByTagName('summary')[0]?.textContent || 'No summary';
          const authors = Array.from(entry.getElementsByTagName('author')).map(
            author => author.getElementsByTagName('name')[0]?.textContent || 'Unknown'
          );
          const categories = Array.from(entry.getElementsByTagName('category')).map(
            cat => cat.getAttribute('term') || ''
          );
          const published = entry.getElementsByTagName('published')[0]?.textContent || '';
          const arxivId = entry.getAttribute('arxiv:id') || entry.getElementsByTagName('id')[0]?.textContent || '';

          return {
            title: title.trim(),
            summary: summary.trim(),
            authors,
            categories,
            published,
            arxivId,
            source: 'arxiv'
          };
        });

        setResults(entries);
        setSize(xmlDoc.getElementsByTagName('opensearch:totalResults')[0]?.textContent);
        setStartindex(parseInt(xmlDoc.getElementsByTagName('opensearch:startIndex')[0]?.textContent || 0) + 1);
      } catch (err) {
        setError(`Error fetching results: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [q, source, sortby, sortOrder, currentPage]);

  return (
    <div className="search-results-container">
      {/* Main Content Area */}
      <div className="results-main">

        {loading && <div className="loading">Loading results...</div>}
        {error && <div className="error">{error}</div>}

        {!loading && !error && (
          <>
            <h2>{startindex} - {startindex + results.length - 1} of {size} Papers for <span className="query-text">{q}</span></h2>
            <ol className="pagination">
              {currentPage > 0 && <li><button onClick={() => setCurrentPage(currentPage - 1)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#0066cc', textDecoration: 'underline' }}>← Previous</button></li>}
              {Array.from({ length: Math.min(10, Math.ceil(size / 10)) }, (_, i) => i).map(page => (
                <li key={page} className={page === currentPage ? 'current' : ''}>
                  {page === currentPage ? <span>{page + 1}</span> : <button onClick={() => setCurrentPage(page)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#0066cc', textDecoration: 'underline' }}>{page + 1}</button>}
                </li>
              ))}
              {Math.ceil(size / 10) > 10 && <li>...</li>}
              {(currentPage + 1) * 10 < size && <li><button onClick={() => setCurrentPage(currentPage + 1)} style={{ cursor: 'pointer', background: 'none', border: 'none', color: '#0066cc', textDecoration: 'underline' }}>Next →</button></li>}
            </ol>
            {results.length === 0 ? (
              <p className="no-results">No results found.</p>
            ) : (
              <div className="results-grid">
                {results.map((result, index) => {
                  // Limit to 3 authors
                  if (result.authors.length > 3) {
                    result.authors = result.authors.slice(0, 3);
                    result.authors.push('et al.');
                  }
                  const displayAuthors = result.authors.join(', ');
                  return (
                    <article key={index} className="result-card">
                      <div className="result-header">
                        <h3 className="result-title">
                          {result.source === 'arxiv' ? (
                            <a href={`https://arxiv.org/abs/${result.arxivId}`} className="title-text">{result.title}</a>
                          ) : (
                            <span className="title-text">{result.title}</span>
                          )}
                          <span className="title-by"> by </span>
                          <span className="title-authors">{displayAuthors}</span>
                        </h3>
                        <div className="result-published">
                          {new Date(result.published).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="result-categories">
                        {result.categories.map((cat, idx) => (
                          <span key={idx} className="category-tag">
                            {cat}
                          </span>
                        ))}
                      </div>

                      <div className="result-summary">
                        <p>{result.summary}</p>
                      </div>

                      <div className="result-footer">
                        {result.source === 'arxiv' ? (
                          <a href={`https://arxiv.org/abs/${result.arxivId}`} target="_blank" rel="noopener noreferrer" className="result-link">
                            View on arXiv
                          </a>
                        ) : (
                          <a href={`https://doi.org/${result.arxivId.split('/')[1]}`} target="_blank" rel="noopener noreferrer" className="result-link">
                            View Paper
                          </a>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Sidebar */}
      <aside className="results-sidebar">
        <div className="sidebar-section">
          <h3>Source</h3>
          <select value={source} onChange={(e) => handleSourceChange(e.target.value)}>
            <option value="arxiv">arXiv</option>
            <option value="biorxiv">bioRxiv</option>
            <option value="medrxiv">medRxiv</option>
          </select>
        </div>
        <div className="sidebar-section">
          <h3>Sort By</h3>
          <select value={sortby} onChange={(e) => handleSortChange(e.target.value)}>
            <option value="submittedDate">Submitted Date</option>
            <option value="relevance">Relevance</option>
            <option value="lastUpdatedDate">Last Updated Date</option>
          </select>
        </div>
        <div className="sidebar-section">
          <h3>Order</h3>
          <select value={sortOrder} onChange={(e) => handleOrderChange(e.target.value)}>
            <option value="descending">Descending</option>
            <option value="ascending">Ascending</option>
          </select>
        </div>
      </aside>
    </div>
  );
}

export default SearchResults;
